/**
 * business-type-combobox.js
 *
 * Progressive enhancement for the business-type field. Turns a plain text input
 * (name="{prefix}.OtherBusinessType") + hidden id (name="{prefix}.BusinessTypeId")
 * into a type-ahead combobox that does BOTH jobs:
 *   - type to search the known trades (data-trades on the wrapper) and pick one
 *     → the hidden id is set to that trade's id;
 *   - type something new → the hidden id stays on the "Other" sentinel
 *     (data-other-id) and the free text is submitted as OtherBusinessType, which
 *     the server dedupes-or-creates (and caches) as a pending business type.
 *
 * No JS: the field is a normal text input with the hidden id defaulted to the
 * sentinel, so a typed trade still submits and is deduped/created server-side.
 *
 * Runs on DOMContentLoaded and after HTMX swaps. Idempotent per wrapper.
 */
(function () {
    'use strict';

    var MAX_SUGGESTIONS = 8;

    function initOne(wrapper) {
        if (wrapper._btInit) return;
        wrapper._btInit = true;

        var input  = wrapper.querySelector('[data-bt-input]');
        var hidden = wrapper.querySelector('[data-bt-id]');
        var list   = wrapper.querySelector('[data-bt-list]');
        var otherId = wrapper.getAttribute('data-other-id') || '';

        var trades = [];
        try { trades = JSON.parse(wrapper.getAttribute('data-trades') || '[]'); }
        catch (e) { trades = []; }

        if (!input || !hidden || !list) return;

        var activeIndex = -1;
        var current = [];   // currently rendered suggestions

        function norm(s) { return (s || '').trim().toLowerCase(); }

        function exactMatchId(text) {
            var n = norm(text);
            for (var i = 0; i < trades.length; i++) {
                if (norm(trades[i].name) === n) return String(trades[i].id);
            }
            return null;
        }

        /** Keep the hidden id in sync: a known trade → its id, otherwise the sentinel. */
        function syncHidden() {
            var id = exactMatchId(input.value);
            hidden.value = id !== null ? id : otherId;
        }

        function close() {
            list.classList.add('hidden');
            list.innerHTML = '';
            input.setAttribute('aria-expanded', 'false');
            activeIndex = -1;
            current = [];
        }

        function render(items) {
            list.innerHTML = '';
            items.forEach(function (t, i) {
                var li = document.createElement('li');
                li.className = 'bt-option';
                li.setAttribute('role', 'option');
                li.id = list.id + '-opt-' + i;
                li.textContent = t.name;
                li.addEventListener('mousedown', function (e) {
                    // mousedown (not click) so it fires before the input's blur.
                    e.preventDefault();
                    choose(t);
                });
                list.appendChild(li);
            });
            current = items;
            activeIndex = -1;
            if (items.length) {
                list.classList.remove('hidden');
                input.setAttribute('aria-expanded', 'true');
            } else {
                close();
            }
        }

        function choose(t) {
            input.value = t.name;
            hidden.value = String(t.id);
            close();
        }

        function filter() {
            var q = norm(input.value);
            if (!q) { close(); return; }
            var matches = [];
            for (var i = 0; i < trades.length && matches.length < MAX_SUGGESTIONS; i++) {
                if (norm(trades[i].name).indexOf(q) !== -1) matches.push(trades[i]);
            }
            render(matches);
        }

        function setActive(idx) {
            var opts = list.querySelectorAll('.bt-option');
            opts.forEach(function (o) { o.classList.remove('is-active'); o.setAttribute('aria-selected', 'false'); });
            activeIndex = idx;
            if (idx >= 0 && idx < opts.length) {
                opts[idx].classList.add('is-active');
                opts[idx].setAttribute('aria-selected', 'true');
                input.setAttribute('aria-activedescendant', opts[idx].id);
                opts[idx].scrollIntoView({ block: 'nearest' });
            } else {
                input.removeAttribute('aria-activedescendant');
            }
        }

        input.addEventListener('input', function () {
            filter();
            syncHidden();
        });

        input.addEventListener('focus', function () {
            if (input.value) filter();
        });

        input.addEventListener('keydown', function (e) {
            var open = !list.classList.contains('hidden');
            switch (e.key) {
                case 'ArrowDown':
                    if (!open) { filter(); return; }
                    e.preventDefault();
                    setActive(Math.min(activeIndex + 1, current.length - 1));
                    break;
                case 'ArrowUp':
                    if (!open) return;
                    e.preventDefault();
                    setActive(Math.max(activeIndex - 1, 0));
                    break;
                case 'Enter':
                    // Always prevent the default here — this is a type-ahead field, not a
                    // submit trigger. Without this, Enter with the suggestion list closed (or
                    // no item highlighted) fell through to the browser's native "submit the
                    // nearest form" behaviour, submitting the form before the visitor meant to.
                    e.preventDefault();
                    if (open && activeIndex >= 0 && current[activeIndex]) {
                        choose(current[activeIndex]);
                    } else {
                        close();
                        syncHidden();
                    }
                    break;
                case 'Escape':
                    close();
                    break;
            }
        });

        input.addEventListener('blur', function () {
            // Delay so a click/mousedown on an option is handled first.
            setTimeout(function () { close(); syncHidden(); }, 120);
        });

        // Establish the initial hidden id from any prefilled value.
        syncHidden();
    }

    function initAll(root) {
        (root || document).querySelectorAll('[data-bt-combobox]').forEach(initOne);
    }

    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
    document.body.addEventListener('htmx:afterSettle', function (e) { initAll(e.target || document); });
})();
